import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { Button, EmptyState, Icon, Page, Spinner, CountBadge } from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import { useOpsCalendar } from "../../app/hooks/useShows.js";
import { useCapability } from "../../app/bandContext.js";
import { usePhoneLayout } from "../../app/phoneLayout.js";
import { ApiError } from "../../app/hooks/apiError.js";
import { NewDealModal } from "../org/NewDealModal.js";
import { PastShowsCard } from "./PastShowsCard.js";
import { CalendarEventDetails, type OpenCalendarEvent } from "./CalendarEventDetails.js";
import { todayIso } from "./lib/bookingRows.js";
import styles from "./OpsCalendarScreen.module.scss";

type OpsEvent = components["schemas"]["OpsCalendarEvent"];

// The schedule kinds the money-free ops-calendar returns. Legend/marker order is this array's order, and it
// is the spec's own legend order (show · hold · rehearsal · content · pr · off) with the two kinds our model
// has and the spec's does not — `festival` (a real `Show.Kind`) and `travel` (a real `TourDay.Kind`, which
// legacy folded into one "OFF / TRAVEL" entry) — slotted beside their families.
//
// Colour per kind lives in @cntrl/tokens (--ac-color-event-*), transcribed from the design spec's own key.
// SEVEN swatches over these eight kinds — off and travel share one, deliberately; see LEGEND below. Two OTHER
// pairs were the same colour by ACCIDENT until 2026-09-09, which on a legend of eight labels reads as the
// colour meaning nothing. Colour is never the sole signal either: each legend swatch is labelled, every grid
// bar prints its own city, and every day cell names each event's kind AND headline in its aria-label.
//
// The key also carries a blue TOUR swatch we deliberately do NOT render: `tour` is a legacy opportunity type
// and our own vocabulary is rehearsal / content / pr / other, so a TOUR row could never appear — a legend
// entry with no source is the same defect as a kind with no legend entry, pointing the other way.
const KINDS = ["show", "festival", "hold", "rehearsal", "content", "pr", "off", "travel"] as const;
type Kind = (typeof KINDS)[number];
const KIND_LABEL: Record<Kind, string> = {
  show: "Show", festival: "Festival", hold: "Hold", rehearsal: "Rehearsal",
  content: "Content", pr: "PR", off: "Off", travel: "Travel",
};
const KIND_SET = new Set<string>(KINDS);
const isKind = (t: string | null | undefined): t is Kind => t != null && KIND_SET.has(t);

// THE LEGEND IS ONE ROW PER SWATCH, NOT PER KIND — seven rows over eight kinds, in the key's own order.
// `off` and `travel` are ONE thing to a reader (operator, 2026-09-09) and the key prints them as a single
// "OFF / TRAVEL" entry, the one label that is not just an uppercased kind. They keep their own KIND, because
// the rail genuinely distinguishes them ("Nashville → Austin" under a TRAVEL pill, "Day off in Houston" under
// an OFF one) and the aria-label says which; what they share is the colour, which is all a legend is.
// A row per kind would put two labels on one swatch and read as a palette that means nothing.
const LEGEND: { kind: Kind; label: string }[] = [
  { kind: "show", label: "Show" },
  { kind: "festival", label: "Festival" },
  { kind: "hold", label: "Hold" },
  { kind: "rehearsal", label: "Rehearsal" },
  { kind: "content", label: "Content" },
  { kind: "pr", label: "PR" },
  { kind: "off", label: "Off / Travel" },
];

// ...and WHICH of those rows this reader gets is the SERVER's answer, because the audience narrows the
// sources (PO, 2026-09-11: a label sees no holds and no tour days). A legend entry with no possible source is
// the same defect as a kind with no legend entry, pointing the other way — HOLD / OFF / TRAVEL swatches on a
// calendar that can never show one read as a calendar that is withholding something. Deriving the audience
// here instead would be a second implementation of a rule the payload already carries. A response without
// `kinds` (an older server, a fixture) falls back to the whole legend, which is exactly the prior behaviour.
function legendFor(kinds: readonly string[] | null | undefined): { kind: Kind; label: string }[] {
  if (!kinds?.length) return LEGEND;
  const served = new Set(kinds);
  // The OFF / TRAVEL row earns its place if EITHER kind is served — it is one swatch over two kinds, so
  // testing only its own `kind` would drop it from a calendar that carries travel days and no off days.
  return LEGEND.filter(({ kind }) => (kind === "off" ? served.has("off") || served.has("travel") : served.has(kind)));
}

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"] as const;
const COMING_UP_MAX = 6;
// How many event bars a day cell prints before it collapses the rest into "+N". TWO, and the number is a
// HEIGHT decision rather than a taste one: six rows have to fit a 16:9 laptop without the page scrolling, and
// two bars is exactly what `.cell`'s 68px floor holds — a third would push every populated day past it and
// make the six rows size themselves individually. A day with more than two is one tap from the selected-day
// strip, which lists all of them.
const CELL_EVENT_MAX = 2;
// The phone draws dashes, not labelled bars, so three fit under the number (the handoff's mobile rule).
const CELL_EVENT_MAX_MOBILE = 3;
// HOW MUCH CALENDAR IS FETCHED, and it is a year either side because the past was the reported bug
// (PO, 2026-09-11: "past dates aren't showing"). Month paging is CLIENT-SIDE over one fetched window — that
// is what keeps it flicker-free — so the window is also, silently, the limit of how far you can page: it used
// to be 31 days back, so the second press of "<" landed on a grid that was empty because nothing had been
// asked for, which renders exactly like a band with no history. The nav buttons now disable at the edge
// rather than paging into that lie, so the window and the affordance agree.
const WINDOW_DAYS = 365;

// Six full weeks. The grid is ALWAYS this many cells — the spec's first "easy to get wrong" rule — so
// leading and trailing days from the neighbouring months always render, muted but live. A ragged grid changes
// height between months and, worse, hides an event on the 31st the moment you page forward, even though the
// same week-row is on screen and the row is already in the fetched window.
const GRID_CELLS = 42;

// "Today" is device-local `YYYY-MM-DD` — never UTC; the viewer's timezone wins. The helper moved to
// `lib/bookingRows.ts` so the Booking tab, the show editor and this calendar all read ONE authority (they
// had drifted: the Booking side was constructing it from `toISOString()`, which rolls over at 19:00 CDT).
/** `YYYY-MM-DD` from local Y/0-based-M/D. */
function isoKey(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}
/** Zoneless domain date: parse `YYYY-MM-DD` at local noon (never a trailing `Z`), so the device timezone wins. */
function parseDomainDate(iso: string): Date {
  return new Date(`${iso}T12:00:00`);
}
/** `YYYY-MM-DD` n days from an ISO day, through the same local-noon parse — so a DST boundary inside the
 *  window cannot shift the date by one. */
function shiftIso(iso: string, days: number): string {
  const d = parseDomainDate(iso);
  d.setDate(d.getDate() + days);
  return isoKey(d.getFullYear(), d.getMonth(), d.getDate());
}
/** The event's day key (`YYYY-MM-DD`), tolerant of a date-time value. */
const dayKey = (ev: OpsEvent): string => (ev.date ?? "").slice(0, 10);

/**
 * The read-only ops calendar — the Production/Content/Merch/crew/Label view, rebuilt to the CD calendar
 * brief: a 42-cell month GRID (today crimson pill + per-day colour markers) with a kind legend, a
 * selected-day strip, and a "Coming Up" list. MONEY-FREE BY CONSTRUCTION: the endpoint returns only the
 * schedule shape (dates, kinds, venue/city, route + call times, tour grouping); there is no money field to
 * strip. The server picks this money-free variant by capability — this surface literally cannot show money.
 *
 * ONE SCREEN, TWO SHELLS, and the two places the spec splits them are settled here rather than branched:
 * a day carries a BAR PER EVENT (the spec's own note calls desktop's single first-event dot an information
 * loss — "the rehearsal is invisible until you select the day"), and Coming Up is the desktop window
 * (`>= today`, six) rather than mobile's `> selected`, five. Ratified 2026-09-05.
 *
 * A DAY'S EVENTS ARE LINE ITEMS, NOT DOTS (2026-09-09). A coloured dot says a day is busy and nothing else, so
 * the grid — the thing a calendar is FOR — could not answer "where are we on the 18th" without a click, and the
 * cities were sitting in the same payload the dots were built from. Each bar is the kind's colour, full width,
 * carrying the row's own headline ("Austin, TX"). Below the phone breakpoint the label is DROPPED rather than
 * truncated and the bars become the spec's own 4px `.sc-cal__strip` segments — three clipped characters say
 * less than a coloured bar does, and the aria-label is unchanged either way.
 *
 * The six-week grid is sized to fit a 16:9 laptop without the page scrolling (`.cell`'s min-height, and the
 * CELL_EVENT_MAX cap that follows from it). It used to be six rows of SQUARES, which at a wide 1fr column ran
 * the card past 1000px — so the legend, the thing that explains the colours, was permanently below the fold.
 *
 * WHOSE calendar this is no longer branches at all (PO, 2026-09-11). It used to be a Whole-band / My-dates
 * toggle defaulting per shell; it is now a fact about the seat, resolved server-side — a band seat sees only
 * the dates it is invited to, a label sees the booked show dates, the content days and any date somebody from
 * the label was invited on. The screen renders whichever answer it is given and offers no way to ask for
 * another, which is also what keeps the client from holding a second opinion about a server narrow.
 *
 * Month navigation is client-side over the fetched window (no per-month refetch → no flicker), and the window
 * is a YEAR either side of today. That pairing is the whole of the "past dates aren't showing" report: the
 * window was 31 days back, so paging past it hit a grid that was empty because nothing had been asked for.
 * The chevrons disable at the window's edge, so there is no month you can reach and not trust.
 */
export function OpsCalendarScreen({ initialDate, initialShowId }: {
  initialDate?: string;
  /** `?show=` — open that show's event sheet (a "you've been paid" notification lands here, ART-305 v3). */
  initialShowId?: string;
} = {}) {
  const isMobile = usePhoneLayout();
  // The phone's one add-a-date affordance is "New opportunity" — the ONLY create mobile permits (spec §3
  // "mobile books nothing"; shows are desktop-only). Gated on the same token the server gates the create
  // endpoint on and the mobile home "+" already reads, so the offered "+" and the accepted POST cannot drift.
  const canCreateOpp = useCapability("opportunities:create");
  const [oppOpen, setOppOpen] = useState(false);

  const todayKey = useMemo(() => todayIso(), []);
  // The fetched window, computed HERE and sent explicitly rather than left to the server's default: the
  // month nav disables at its edge, so the client has to know exactly where that edge is. Two authorities for
  // one window is how a disabled button and an empty grid come to disagree about the same month.
  const windowFrom = useMemo(() => shiftIso(todayKey, -WINDOW_DAYS), [todayKey]);
  const windowTo = useMemo(() => shiftIso(todayKey, WINDOW_DAYS), [todayKey]);
  const cal = useOpsCalendar(windowFrom, windowTo);

  // `initialDate` (a deep link — the chat's Day of Show link) opens on that day, when it lies inside the fetched
  // window; outside it the grid could only render a month it never asked for, so it falls back to today.
  const opening = initialDate && initialDate >= windowFrom && initialDate <= windowTo ? initialDate : todayKey;
  const openingDate = parseDomainDate(opening);
  const [year, setYear] = useState(() => openingDate.getFullYear());
  const [month, setMonth] = useState(() => openingDate.getMonth()); // 0-based
  const [selected, setSelected] = useState(opening);
  // ART-305: the ONE event whose details are open (a pop-up on desktop, a sheet on the phone). Opening one also
  // selects its day.
  const [openEvent, setOpenEvent] = useState<OpenCalendarEvent | null>(
    initialShowId ? { kind: "show", id: initialShowId, anchor: null } : null,
  );
  const cellMax = isMobile ? CELL_EVENT_MAX_MOBILE : CELL_EVENT_MAX;

  function openDetails(ev: OpsEvent, anchor: Element | null) {
    const ref = itemRef(ev);
    if (!ref) return;
    setSelected(dayKey(ev));
    setOpenEvent({ ...ref, anchor: anchor?.getBoundingClientRect() ?? null });
  }
  const isOpen = (ev: OpsEvent) => {
    const ref = itemRef(ev);
    return !!ref && !!openEvent && ref.kind === openEvent.kind && ref.id === openEvent.id;
  };
  // "INVITED · NEEDS A REPLY" joins the legend while the calendar actually holds an invite to answer — only a
  // crew calendar ever does, so a label never sees a swatch with no possible source.
  const hasInvites = useMemo(() => (cal.data?.events ?? []).some((ev) => ev.invited), [cal.data]);

  // Events grouped by their day key, once per data change.
  const byDate = useMemo(() => {
    const map = new Map<string, OpsEvent[]>();
    for (const ev of cal.data?.events ?? []) {
      const key = dayKey(ev);
      if (!key) continue;
      const bucket = map.get(key);
      if (bucket) bucket.push(ev);
      else map.set(key, [ev]);
    }
    return map;
  }, [cal.data]);

  // Upcoming events (today onward), date-ascending — the "Coming Up" list. ISO date strings sort lexically.
  const upcoming = useMemo(() => {
    return (cal.data?.events ?? [])
      .filter((ev) => dayKey(ev) >= todayKey)
      .sort((a, b) => dayKey(a).localeCompare(dayKey(b)))
      .slice(0, COMING_UP_MAX);
  }, [cal.data, todayKey]);

  // The 42 cells of the six-week grid, built from `1 - startDow` so the neighbouring months' days are real
  // dates rather than padding: muted, but selectable and carrying their own markers.
  const cells = useMemo(() => {
    const startDow = new Date(year, month, 1).getDay();
    return Array.from({ length: GRID_CELLS }, (_, i) => {
      const d = new Date(year, month, 1 - startDow + i);
      return { key: isoKey(d.getFullYear(), d.getMonth(), d.getDate()), day: d.getDate(), muted: d.getMonth() !== month };
    });
  }, [year, month]);

  const monthLabel = new Date(year, month, 1).toLocaleString(undefined, { month: "long", year: "numeric" });

  const selectedDate = parseDomainDate(selected);
  const selectedInView = selectedDate.getFullYear() === year && selectedDate.getMonth() === month;
  const selectedLabel = selectedDate.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  const selectedEvents = byDate.get(selected) ?? [];

  // A month is reachable while ANY of its days sit inside the fetched window — so the edge months render
  // partially, which is honest, rather than being refused because they are not wholly loaded.
  function monthInWindow(delta: number): boolean {
    const first = new Date(year, month + delta, 1);
    const last = new Date(year, month + delta + 1, 0);
    return isoKey(last.getFullYear(), last.getMonth(), last.getDate()) >= windowFrom
      && isoKey(first.getFullYear(), first.getMonth(), first.getDate()) <= windowTo;
  }
  const canStepBack = monthInWindow(-1);
  const canStepForward = monthInWindow(1);

  function stepMonth(delta: number) {
    if (!monthInWindow(delta)) return;
    const d = new Date(year, month + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth());
  }
  /** "Today" resets BOTH the view month and the selection — the spec is explicit that it is not just a page. */
  function goToday() {
    const d = parseDomainDate(todayKey);
    setYear(d.getFullYear());
    setMonth(d.getMonth());
    setSelected(todayKey);
  }

  /** The day's events in the order the read returned them. An unknown/null type falls back to "show" via
   * `kindOf` — same as statusPill/eventLine — so a day that HAS an event always yields a bar (it never renders,
   * or reads to a screen reader, as "no events" while the strip/Coming Up list it). */
  function eventsOn(key: string): OpsEvent[] {
    return byDate.get(key) ?? [];
  }

  return (
    // `<Page>` owns the vertical rhythm + the mobile-gated side gutter (the shell supplies none); hand-rolling
    // it is what the pageShell guard forbids (ART-153 pass two). No `title` — this surface is named by the nav.
    <Page>
      {/* No page header. The surface is named by the shell's nav (the "Calendar"/"Schedule" leaf) and, under
          the roster lens, by the shell's own context header — an h1 here was a second name for one page, and
          the two did not even agree on the word. Legacy's desktop Calendar has no heading either; it carries
          `data-screen-label` and nothing visible. */}

      {cal.isError && !cal.data ? (
        // Only the FIRST-load failure (no cached data) replaces the calendar. A failed background refetch flips
        // status to error while retaining the last-good events (TanStack v5) — keep the grid + selection rather
        // than wiping good schedule data on a transient blip (mirrors the "clear only on real failure" posture).
        <EmptyState
          title="Couldn't load the calendar"
          description={cal.error instanceof ApiError ? cal.error.message : "Please try again."}
          action={<Button onClick={() => void cal.refetch()}>Retry</Button>}
        />
      ) : (
        <div className={styles.layout}>
          <section className={styles.calCard} aria-label="Month calendar" aria-busy={cal.isLoading || undefined}>
            <div className={styles.calHead}>
              <span className={styles.monthLabel}>{monthLabel}</span>
              {/* No view-mode toggle. Whose calendar this is comes from the caller's own seat and is settled
                  server-side (PO, 2026-09-11) — a band seat sees the dates it is invited to, a label sees the
                  booked shows, the content days and whatever a colleague was invited on. A client control
                  over a server narrow is a second authority on a question only the server can answer, and it
                  let a crew member widen their calendar to the band's whole routing with one click. */}
              <div className={styles.calControls}>
                <div className={styles.calNav}>
                  {/* Disabled at the fetched window's edge rather than paging into an empty grid — an
                      unfetched month and a month with nothing in it are the same pixels. */}
                  <button
                    type="button" className={styles.navBtn} onClick={() => stepMonth(-1)}
                    disabled={!canStepBack} aria-label="Previous month"
                  >
                    <Icon icon={ChevronLeft} size={16} />
                  </button>
                  {/* Desktop keeps "Today" — a big paged calendar needs the jump back. The phone drops it for
                      "< > +" (Ryan, ART-192): the + is the add-a-date affordance and Today is redundant when
                      the selected-day strip already anchors the view. */}
                  {!isMobile && (
                    <button type="button" className={styles.todayBtn} onClick={goToday}>Today</button>
                  )}
                  <button
                    type="button" className={styles.navBtn} onClick={() => stepMonth(1)}
                    disabled={!canStepForward} aria-label="Next month"
                  >
                    <Icon icon={ChevronRight} size={16} />
                  </button>
                  {/* Mobile "+" → New opportunity (see canCreateOpp). Hidden when the seat can't create one,
                      exactly as the mobile home "+" hides — an offered create that the POST would refuse is a
                      dead button. The fuller "add a date" flow Ryan flagged on the ticket is a follow-up. */}
                  {isMobile && canCreateOpp && (
                    <button
                      type="button" className={styles.addBtn} onClick={() => setOppOpen(true)}
                      aria-label="Add opportunity"
                    >
                      <Icon icon={Plus} size={16} />
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.grid} role="group" aria-label={`${monthLabel} calendar`}>
              {WEEKDAYS.map((d, i) => (
                <span key={`dow-${i}`} className={styles.dow} aria-hidden>{d}</span>
              ))}
              {cells.map(({ key, day, muted }) => {
                const evs = eventsOn(key);
                const isToday = key === todayKey;
                const isSelected = key === selected;
                // The accessible name carries every event on the day, kind AND headline — the same headline the
                // bar prints, so the two channels say one thing. It is NOT capped at CELL_EVENT_MAX: that cap is
                // a pixel budget, and a screen reader has no such budget.
                const aria = `${parseDomainDate(key).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}${isToday ? ", today" : ""}${
                  evs.length
                    ? `, ${evs.map((ev) => `${KIND_LABEL[kindOf(ev)]} — ${eventLine(ev).primary}`).join(", ")}`
                    : ", no events"
                }`;
                const overflow = evs.length - cellMax;
                return (
                  // The CELL is a frame, not a button: a day selects through its stretched overlay button, and on
                  // desktop each event bar is its OWN button above it that opens the event's details (ART-305) —
                  // a control inside a control is invalid markup, so the two sit side by side.
                  <div
                    key={key}
                    className={styles.cell}
                    data-today={isToday || undefined}
                    data-selected={isSelected || undefined}
                    data-muted={muted || undefined}
                  >
                    <button
                      type="button"
                      className={styles.cellSelect}
                      aria-pressed={isSelected}
                      aria-label={aria}
                      onClick={() => setSelected(key)}
                    />
                    <span className={styles.dayNum} aria-hidden>{day}</span>
                    {evs.length > 0 && (
                      // The bars are hidden from assistive tech on the phone (the day button already names every
                      // event); on desktop each is a real, named button.
                      <span className={styles.bars} aria-hidden={isMobile || undefined}>
                        {evs.slice(0, cellMax).map((ev, i) => {
                          const line = eventLine(ev).primary;
                          const ref = itemRef(ev);
                          return isMobile || !ref ? (
                            <span
                              key={`${key}-${i}`}
                              className={`${styles.eventBar} ${styles.kind}`}
                              data-kind={kindOf(ev)}
                              data-invited={ev.invited || undefined}
                              title={line}
                            >
                              {line}
                            </span>
                          ) : (
                            <button
                              key={`${key}-${i}`}
                              type="button"
                              className={`${styles.eventBar} ${styles.barButton} ${styles.kind}`}
                              data-kind={kindOf(ev)}
                              data-invited={ev.invited || undefined}
                              data-open={isOpen(ev) || undefined}
                              title={line}
                              aria-label={`${ev.invited ? "Invite — " : ""}${KIND_LABEL[kindOf(ev)]} — ${line}: details`}
                              onClick={(e) => openDetails(ev, e.currentTarget)}
                            >
                              {line}
                            </button>
                          );
                        })}
                        {overflow > 0 && <span className={styles.moreBar}>+{overflow} more</span>}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            <ul className={styles.legend} aria-label="Legend">
              {legendFor(cal.data?.kinds).map(({ kind, label }) => (
                <li key={kind} className={styles.legendItem}>
                  <span className={`${styles.swatch} ${styles.kind}`} data-kind={kind} aria-hidden />
                  <span className={styles.legendLabel}>{label}</span>
                </li>
              ))}
              {hasInvites && (
                <li className={styles.legendItem}>
                  <span className={`${styles.swatch} ${styles.kind}`} data-kind="show" data-invited aria-hidden />
                  <span className={styles.legendLabel}>{isMobile ? "Invited" : "Invited · needs a reply"}</span>
                </li>
              )}
            </ul>
          </section>

          <div className={styles.side}>
            {/* Selected-day strip */}
            <section className={styles.dayStrip} aria-label="Selected day">
              {/* Date left, event COUNT right. On the phone the count is a small top-right tag (Ryan,
                  ART-192: "event count top right"); on desktop the header stacks and the count reads as the
                  strip's display heading, which is the prior `.dayTitle` look. */}
              <div className={styles.dayStripHead}>
                <p className={styles.dayEyebrow}>
                  {selectedLabel}
                  {selected === todayKey && <span className={styles.todayTag}> · Today</span>}
                  {!selectedInView && <span className={styles.offMonth}> · {selectedDate.toLocaleString(undefined, { month: "short", year: "numeric" })}</span>}
                </p>
                {!cal.isLoading && selectedEvents.length > 0 && (
                  <span className={styles.dayCount}>
                    {selectedEvents.length} event{selectedEvents.length > 1 ? "s" : ""}
                  </span>
                )}
              </div>
              {cal.isLoading ? (
                <div className={styles.center} role="status" aria-live="polite"><Spinner /></div>
              ) : selectedEvents.length === 0 ? (
                <>
                  {/* Two different strings, per the spec: an empty TODAY is "No shows today"; any other empty
                      day is "Nothing scheduled". One string for both loses the only thing the line was saying. */}
                  <h3 className={styles.dayTitle}>{selected === todayKey ? "No shows today" : "Nothing scheduled"}</h3>
                  <div className={styles.dayEmpty}>No events on this day.</div>
                </>
              ) : (
                <ul className={styles.dayList}>
                  {selectedEvents.map((ev, i) => {
                    const line = eventLine(ev);
                    const openable = !!itemRef(ev);
                    return (
                      <li key={`${selected}-${i}`} className={openable ? `${styles.dayRow} ${styles.rowLink}` : styles.dayRow}>
                        <span className={styles.dayRowMain}>
                          <span className={styles.dayRowTitle}>{line.primary}</span>
                          {line.secondary && <span className={styles.dayRowSub}>{line.secondary}</span>}
                        </span>
                        <RowPill ev={ev} />
                        {openable && (
                          <button type="button" className={styles.rowButton}
                            aria-label={`Details — ${line.primary}`}
                            onClick={(e) => openDetails(ev, e.currentTarget)} />
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            {/* Coming Up */}
            <section className={styles.comingUp} aria-labelledby="coming-up-heading">
              <div className={styles.comingUpHead}>
                <p id="coming-up-heading" className={styles.eyebrow}>Coming Up</p>
                <CountBadge count={upcoming.length} className={styles.upCount} />
              </div>
              {cal.isLoading ? (
                <div className={styles.center} role="status" aria-live="polite"><Spinner /></div>
              ) : upcoming.length === 0 ? (
                <div className={styles.dayEmpty}>Nothing upcoming yet.</div>
              ) : (
                <ul className={styles.upList}>
                  {upcoming.map((ev, i) => {
                    const line = eventLine(ev);
                    const d = parseDomainDate(dayKey(ev));
                    // Every row opens the event's details (ART-305) — on the phone that sheet carries "Open DOS" for a
                    // show, which is where this row used to jump straight to (ART-251).
                    const openable = !!itemRef(ev);
                    return (
                      <li key={`${dayKey(ev)}-${i}`}
                        className={openable ? `${styles.upRow} ${styles.rowLink}` : styles.upRow}>
                        <span className={styles.upDate}>
                          <span className={styles.upDow}>{d.toLocaleDateString(undefined, { weekday: "short" })}</span>
                          <span className={styles.upDay}>{d.getDate()}</span>
                        </span>
                        <span className={styles.upMain}>
                          <span className={styles.upTitle}>{line.primary}</span>
                          {line.secondary && <span className={styles.upSub}>{line.secondary}</span>}
                        </span>
                        <RowPill ev={ev} />
                        {openable && (
                          // A stretched overlay, not a wrapper button: the row keeps its own layout, and the
                          // button is the whole row's hit area with a name that says where it goes.
                          <button type="button" className={styles.rowButton}
                            aria-label={`Details — ${line.primary}`}
                            onClick={(e) => openDetails(ev, e.currentTarget)} />
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            {/* Past shows — MOBILE ONLY, because the desktop equivalent is Booking's Past tab and two copies of
                one list is how they come to disagree. Self-hides when there is nothing behind it. */}
            {isMobile && (
              <PastShowsCard onOpenShow={(id, el) => setOpenEvent({ kind: "show", id, anchor: el.getBoundingClientRect() })} />
            )}
          </div>
        </div>
      )}
      {openEvent && <CalendarEventDetails open={openEvent} onClose={() => setOpenEvent(null)} />}
      {oppOpen && (
        // The phone's add-a-date flow. Seeded to the selected day so "+" on a tapped date fills that date in.
        <NewDealModal
          title="New opportunity"
          onClose={() => setOppOpen(false)}
          seedDate={selected}
        />
      )}
    </Page>
  );
}

/** Which table a row's details live in, from the one id it carries — null for a row with none (an older server). */
function itemRef(ev: OpsEvent): { kind: "show" | "tour_day" | "opportunity"; id: string } | null {
  if (ev.showId) return { kind: "show", id: ev.showId };
  if (ev.tourDayId) return { kind: "tour_day", id: ev.tourDayId };
  if (ev.opportunityId) return { kind: "opportunity", id: ev.opportunityId };
  return null;
}

/** A row's pill: the kind, or — for an invite still waiting on the viewer — an amber INVITED in its place. */
function RowPill({ ev }: { ev: OpsEvent }) {
  if (ev.invited) return <span className={styles.statusPill} data-tone="invited">Invited</span>;
  const pill = statusPill(ev);
  return (
    <span className={styles.statusPill}>
      <span className={`${styles.dot} ${styles.kind}`} data-kind={pill.kind} aria-hidden />
      {pill.label}
    </span>
  );
}

/** A row's kind, with the "unknown/null falls back to show" rule in ONE place — the grid bars, the aria-label,
 *  the status pill and the row headline all key off it, and four copies is how a day comes to be a rehearsal in
 *  its colour and a show in its label. */
function kindOf(ev: OpsEvent): Kind {
  return isKind(ev.type) ? ev.type : "show";
}

/** The status pill for a row (CD: the kind label, uppercased). `kind` drives the pill's leading dot colour. */
function statusPill(ev: OpsEvent): { label: string; kind: Kind } {
  const kind = kindOf(ev);
  // The label is the KIND, uppercased — the spec's `kindLabel.toUpperCase()`. It deliberately does NOT read
  // the row's `status`: "CONF" was mobile card copy leaking onto the desktop rail, where a confirmed show
  // reads SHOW, and it made this one field speak a third vocabulary that neither the legend nor the grid uses.
  return { label: KIND_LABEL[kind].toUpperCase(), kind };
}

/** The two text lines for an event row — city-led like the dashboard Upcoming card, venue as the sub. Born-blank
 *  safe, and the ONE authority for a row's headline: the grid's bars print `primary` too, so a day cell and the
 *  rail beside it cannot name the same date two ways.
 *
 *  The headline is "City, ST" — the same `city, state` join the dashboard Upcoming card builds. `state` rides
 *  the row because `artist.shows` has always stored the two apart and the ops-calendar composer read only the
 *  city until 2026-09-09; it is absent on a tour day (whose "city" is a route) and on an opportunity (whose
 *  headline is what it IS), which is why the join is conditional rather than a format string. */
function eventLine(ev: OpsEvent): { primary: string; secondary: string } {
  const kind = kindOf(ev);
  const cityLine = ev.city ? `${ev.city}${ev.state ? `, ${ev.state}` : ""}` : null;
  const primary = cityLine || ev.title || ev.venueName || KIND_LABEL[kind];
  const secondary = ev.venueName && ev.venueName !== primary
    ? ev.venueName
    : ev.title && ev.title !== primary
      ? ev.title
      : "";
  return { primary, secondary };
}
